import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import UpdateIcon from '@mui/icons-material/Update';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from '../../context/AuthContext';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isCollapsed, onToggle, isMobile = false, isOpen = false, onClose }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [moreExpanded, setMoreExpanded] = useState(false);
  
  const isActive = (path: string) => location.pathname === path;

  const navigationItems = [
    { path: '/', icon: HomeIcon, label: 'Home' },
    { path: '/library', icon: LibraryMusicIcon, label: 'Library' },
    { path: '/search', icon: SearchIcon, label: 'Search' },
    ...(user ? [
      { path: '/analytics', icon: BarChartIcon, label: 'Analytics' }
    ] : []),
  ];

  // Subscription management navigation
  const getSubscriptionNavItem = () => {
    if (!user) return null;
    
    const userTier = user.subscriptionTier || 'free';
    
    if (userTier === 'free') {
      return { path: '/subscription', icon: UpgradeIcon, label: 'Upgrade' };
    } else if (userTier === 'premium') {
      return { path: '/subscription', icon: ManageAccountsIcon, label: 'Downgrade' };
    } else {
      return { path: '/subscription', icon: ManageAccountsIcon, label: 'Subscription' };
    }
  };

  const subscriptionNavItem = getSubscriptionNavItem();

  // More section items
  const moreItems = user ? [
    { path: '/profile', icon: PersonIcon, label: 'Profile' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings' },
    ...(subscriptionNavItem ? [subscriptionNavItem] : [])
  ] : [];

  const bottomItems = [
    { path: '/about', icon: InfoIcon, label: 'About' },
    { path: '/forartists', icon: MusicNoteIcon, label: 'For Artists' },
    { path: '/help', icon: HelpIcon, label: 'Help' },
    { path: '/changelog', icon: UpdateIcon, label: 'Changelog' },
  ];

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`flex-shrink-0 p-6 flex ${isCollapsed ? 'justify-center' : 'justify-between'} items-center border-b border-white/5`}>
        {!isCollapsed && (
          <img src="https://pub-b889e285347e41b093edf65c2f3ff7c1.r2.dev/naiaweblogo.svg" alt="Naia" className="h-10" />
        )}
        <button
          onClick={isMobile ? onClose : onToggle}
          className="p-2 text-slate-grey hover:text-pearl-white hover:bg-white/5 rounded-lg transition-all duration-200 clickable"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isMobile ? <ChevronLeftIcon /> : isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
        </button>
      </div>

      {/* Navigation content */}
      <div className="flex-1 flex flex-col px-3 py-4 min-h-0">
        {/* Main navigation - takes up available space */}
        <div className="flex flex-col space-y-2 mb-6">
          {navigationItems.map(({ path, icon: Icon, label }) => (
            <Tooltip 
              key={path}
              title={isCollapsed ? label : ''}
              placement="right"
              arrow
              disableHoverListener={!isCollapsed}
            >
              <Link
                to={path}
                onClick={isMobile ? onClose : undefined}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                  isActive(path) 
                    ? 'bg-naia-pink text-pearl-white elevation-1' 
                    : 'text-slate-grey hover:text-pearl-white hover:bg-white/5'
                } ${isCollapsed ? 'justify-center' : ''}`}
              >
                <Icon />
                {!isCollapsed && <span className="font-medium">{label}</span>}
              </Link>
            </Tooltip>
          ))}

          {/* Free user upgrade button in main navigation */}
          {user && user.subscriptionTier === 'free' && (
            <Tooltip 
              title={isCollapsed ? 'Upgrade' : ''}
              placement="right"
              arrow
              disableHoverListener={!isCollapsed}
            >
              <Link
                to="/subscription"
                onClick={isMobile ? onClose : undefined}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-naia-pink to-purple-600 text-pearl-white hover:from-pink-500 hover:to-purple-500 ${
                  isActive('/subscription') ? 'elevation-1' : ''
                } ${isCollapsed ? 'justify-center' : ''}`}
              >
                <UpgradeIcon />
                {!isCollapsed && <span className="font-medium">Upgrade</span>}
              </Link>
            </Tooltip>
          )}

          {/* More section for authenticated users */}
          {user && moreItems.length > 0 && (
            <div className="mt-4">
              {!isCollapsed && (
                <button
                  onClick={() => setMoreExpanded(!moreExpanded)}
                  className="w-full flex items-center justify-between px-3 py-2 text-slate-grey hover:text-pearl-white transition-colors"
                >
                  <span className="font-medium text-sm">More</span>
                  {moreExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
              )}
              
              {(moreExpanded || isCollapsed) && moreItems.map(({ path, icon: Icon, label }) => (
                <Tooltip 
                  key={path}
                  title={isCollapsed ? label : ''}
                  placement="right"
                  arrow
                  disableHoverListener={!isCollapsed}
                >
                  <Link
                    to={path}
                    onClick={isMobile ? onClose : undefined}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                      isActive(path) 
                        ? 'bg-naia-pink text-pearl-white elevation-1' 
                        : 'text-slate-grey hover:text-pearl-white hover:bg-white/5'
                    } ${isCollapsed ? 'justify-center' : ''}`}
                  >
                    <Icon />
                    {!isCollapsed && <span className="font-medium">{label}</span>}
                  </Link>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {/* Spacer to push bottom items down */}
        <div className="flex-1" />

        {/* Bottom navigation - fixed at bottom */}
        <div className="flex-shrink-0 pt-4 border-t border-white/10">
          <div className="flex flex-col space-y-2">
            {bottomItems.map(({ path, icon: Icon, label }) => (
              <Tooltip 
                key={path} 
                title={isCollapsed ? label : ''} 
                placement="right"
                arrow
                disableHoverListener={!isCollapsed}
              >
                <Link
                  to={path}
                  onClick={isMobile ? onClose : undefined}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 clickable ${
                    isActive(path)
                      ? 'bg-naia-pink text-pearl-white elevation-1'
                      : 'text-slate-grey hover:text-pearl-white hover:bg-white/5'
                  } ${isCollapsed ? 'justify-center' : ''}`}
                >
                  <Icon />
                  {!isCollapsed && <span className="font-medium text-sm">{label}</span>}
                </Link>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile overlay */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={onClose}
          />
        )}
        
        {/* Mobile sidebar */}
        <div className={`fixed left-0 top-0 h-full w-64 bg-surface-dark border-r border-surface-border z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {sidebarContent}
        </div>
      </>
    );
  }

  // Desktop sidebar
  return (
    <div className={`fixed left-0 top-0 h-full ${isCollapsed ? 'w-16' : 'w-64'} bg-surface-dark border-r border-surface-border flex-shrink-0 transition-all duration-300 ease-in-out hidden md:block elevation-2 z-20`}>
      {sidebarContent}
    </div>
  );
} 